/**
 * Adapt a bucket RolloutTrajectory to the review transcript's event
 * model, so eval-case rollouts render with the same compact
 * TranscriptEvents rows as /review and /conversations.
 *
 * The bucket shape carries no tool_call_id, so tool results pair with
 * calls by order: a `tool` message resolves the oldest still-unanswered
 * call, which matches sequential execution. A role outside the known set
 * degrades to a visible `raw` row, never silence.
 */

import type { ToolEvent, TranscriptEvent } from '@/components/review/transcript'
import type { TrajectoryMessage } from '@/api/generated/model'
import { isRecord } from '@/lib/guards'

/** name/args out of an exported OpenAI-shape tool call (generic JSON). */
function toolCallParts(call: unknown): { name: string; args: string } {
  const fn = isRecord(call) && isRecord(call.function) ? call.function : undefined
  return {
    name: typeof fn?.name === 'string' && fn.name !== '' ? fn.name : 'tool',
    args: typeof fn?.arguments === 'string' ? fn.arguments : JSON.stringify(call),
  }
}

export function trajectoryToEvents(
  messages: TrajectoryMessage[],
  traceId: string
): TranscriptEvent[] {
  const events: TranscriptEvent[] = []
  const pendingCalls: ToolEvent[] = []
  for (const message of messages) {
    switch (message.role) {
      case 'system':
        events.push({ kind: 'system', traceId, text: message.content })
        break
      case 'user':
        events.push({ kind: 'user', traceId, text: message.content, images: [] })
        break
      case 'assistant': {
        if (message.content.trim() !== '') {
          events.push({ kind: 'assistant', traceId, text: message.content })
        }
        for (const call of message.tool_calls ?? []) {
          const { name, args } = toolCallParts(call)
          const event: ToolEvent = {
            kind: 'tool',
            traceId,
            name,
            args,
            result: null,
            resultTraceId: null,
            callId: null,
          }
          events.push(event)
          pendingCalls.push(event)
        }
        break
      }
      case 'tool': {
        const call = pendingCalls.shift()
        if (call !== undefined) {
          call.result = message.content
          call.resultTraceId = traceId
        } else {
          // An output with no preceding call still shows, as itself.
          events.push({ kind: 'raw', traceId, label: 'tool output', text: message.content })
        }
        break
      }
      default:
        events.push({ kind: 'raw', traceId, label: message.role, text: message.content })
    }
  }
  return events
}
